import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { analytics } from "@heycatch/sdk";

const identify = (u) => {
  if (!u?.user_id) return;
  analytics.setIdentity(
    u.user_id,
    { email: u.email, name: u.name, plan: u.plan || u.subscription?.plan || u.subscription_status },
    { signup_date: u.created_at },
  );
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identify(data);
      if (data?.currency) window.__wpm_currency = data.currency;
    } catch (e) {
      // Not authenticated / network — mark as anonymous and continue.
      if (e?.response?.status !== 401) console.warn("auth refresh failed", e);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip auto-check if OAuth callback is in-flight
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    refresh();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    identify(data);
    analytics.trackEvent("signup_completed", { account_type: payload.account_type || "individual" });
    refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout request failed — clearing client session anyway", e); }
    setUser(false);
    analytics.resetIdentity();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
